import { IRowNode } from 'ag-grid-community';
import { Select } from 'antd';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { useCallback, useMemo, useState, memo } from 'react';
import { ExcelTableSelectionOptions } from '../excel-table/types';
import { cn } from 'utils';
import { SortedSelect } from 'components/ui/sorted-select';
import { DefaultOptionType } from 'antd/es/select';

interface AgGridSelectRendererProps<TData> {
  options: ExcelTableSelectionOptions<TData>;
  disabled?: boolean;
  defaultValue?: string | ((data?: TData) => string);
  value: string | null | undefined;
  data: TData | undefined;
  columnId?: string;
  node: IRowNode<TData>;
}

const AgGridSelectRenderer = <TData extends object>({
  options,
  disabled,
  defaultValue,
  ...props
}: AgGridSelectRendererProps<TData>) => {
  const [searchText, setSearchText] = useState('');
  const { value, columnId, node } = props;
  const handleChange = useCallback(
    (selectedValue: string) => {
      if (columnId) {
        node.setDataValue(columnId, selectedValue);
      }
    },
    [columnId, node]
  );

  const renderOptions = useMemo(() => {
    if (typeof options === 'function') {
      if (props.data) {
        return options(props.data).filter((option) =>
          String(option.label).toLowerCase().includes(searchText.toLowerCase())
        );
      }
      return [];
    }
    return options.filter((option) =>
      String(option.label).toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, props.data, searchText]);

  const currentOption = useMemo(
    () => renderOptions.find((option) => option.value === value),
    [renderOptions, value]
  );
  const defaultSelectionValue = useMemo(() => {
    if (typeof defaultValue === 'function') {
      return defaultValue(props.data);
    }
    return defaultValue;
  }, [defaultValue, props.data]);

  return (
    <div style={currentOption?.style}>
      <SortedSelect
        value={value}
        onChange={handleChange}
        className={cn(
          'w-full ag-grid-select',
          disabled && 'ag-grid-select-disabled'
        )}
        suffixIcon={<ArrowDownIcon />}
        showSearch
        onSearch={setSearchText}
        onSelect={handleChange}
        disabled={disabled}
        filterOption={false}
        options={renderOptions as DefaultOptionType[]}
        defaultValue={defaultSelectionValue}
      />
    </div>
  );
};

export default memo(AgGridSelectRenderer) as typeof AgGridSelectRenderer;
